import { cn } from '@/lib/utils';
import { motion, useReducedMotion } from 'framer-motion';
import { useEffect, useRef, useState } from 'react';

const EASE = [0.22, 1, 0.36, 1] as const;

export interface NavSection {
    id: string;
    label: string;
}

interface SectionNavProps {
    sections: NavSection[];
}

/**
 * In-page section subnav, styled to match the /journal sub-nav (JournalNav):
 * transparent until pinned, then a hairline border + translucent blurred bg,
 * with an animated underline on the active item. Differs from JournalNav only
 * in behaviour — these are in-page anchors (scroll + IntersectionObserver
 * scroll-spy) rather than category links.
 */
export default function SectionNav({ sections }: SectionNavProps) {
    const [active, setActive] = useState<string | null>(null);
    const [isPinned, setIsPinned] = useState(false);
    const sentinelRef = useRef<HTMLDivElement>(null);
    const shouldReduceMotion = useReducedMotion();

    // Pin state — mirrors JournalNav (border/bg/blur appear once the nav sticks).
    useEffect(() => {
        const sentinel = sentinelRef.current;
        if (!sentinel) return;
        const observer = new IntersectionObserver(([entry]) => setIsPinned(!entry.isIntersecting), { threshold: 0 });
        observer.observe(sentinel);
        return () => observer.disconnect();
    }, []);

    // Scroll-spy — highlight the in-view section.
    useEffect(() => {
        const els = sections.map((s) => document.getElementById(s.id)).filter((el): el is HTMLElement => !!el);
        const spy = new IntersectionObserver(
            (entries) => {
                const inView = entries.filter((e) => e.isIntersecting).sort((a, b) => b.intersectionRatio - a.intersectionRatio);
                if (inView[0]) setActive(inView[0].target.id);
            },
            { rootMargin: '-30% 0px -60% 0px', threshold: [0, 0.25, 0.5, 1] },
        );
        els.forEach((el) => spy.observe(el));
        return () => spy.disconnect();
    }, [sections]);

    const go = (id: string) =>
        document.getElementById(id)?.scrollIntoView({ behavior: shouldReduceMotion ? 'auto' : 'smooth', block: 'start' });

    return (
        <>
            <div ref={sentinelRef} className="h-0" aria-hidden />
            <nav
                aria-label="Page sections"
                className={cn(
                    'sticky top-14 z-30 border-b border-[#F5F5F5]/10 transition-all duration-300 md:top-20',
                    isPinned ? 'bg-[#0e2727]/95 backdrop-blur-md' : 'bg-[#0e2727]',
                )}
            >
                <div className="nav-px scrollbar-none mx-auto flex items-center gap-1 overflow-x-auto py-3 sm:gap-2">
                    <div className="mx-auto flex items-center gap-1 sm:gap-2">
                        {sections.map((s) => (
                            <button
                                key={s.id}
                                type="button"
                                onClick={() => go(s.id)}
                                className={cn(
                                    'lux-caps lux-caps-sm relative shrink-0 px-3 py-1.5 whitespace-nowrap transition-colors',
                                    active === s.id ? 'text-[#F5F5F5]' : 'text-[#F5F5F5]/50 hover:text-[#F5F5F5]/80',
                                )}
                            >
                                {s.label}
                                {active === s.id && (
                                    <motion.span
                                        layoutId="agent-section-nav-underline"
                                        className="absolute inset-x-1 -bottom-3 h-0.5 bg-[#F5F5F5]"
                                        transition={shouldReduceMotion ? { duration: 0 } : { duration: 0.3, ease: EASE }}
                                    />
                                )}
                            </button>
                        ))}
                    </div>
                </div>
            </nav>
        </>
    );
}
